import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import { CUSTOMER_INVOICE_PERMISSIONS, resolvePermissionCheck } from '@/utils/groupPermissions';
const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: '-creation_date' };

const createState = (options = {}) => ({
    invoiceitems: [],
    invoiceitems_url: null,
    invoiceitems_filters: DEFAULT_FILTERS,
    invoiceitems_count: 0,
    invoiceitems_loaded: false,
    invoiceitems_loading: false,
    invoiceitems_loading_errors: null,

    invoiceitems_stats: {},
    invoiceitems_stats_loaded: false,
    invoiceitems_stats_loading: false,
    invoiceitems_stats_loading_errors: null,
    cancelTokenSource: null,
    cancelTokenSourceStats: null,
    _storageOptions: options || {},
});

const mutations = {
    updateInvoiceItems(state, invoiceitems) {
        state.invoiceitems = invoiceitems;
        state.invoiceitems_loaded = true;
        state.invoiceitems_loading = false;
        state.invoiceitems_loading_errors = null;
    },
    updateInvoiceItemsUrl(state, invoiceitems_url) {
        state.invoiceitems_url = invoiceitems_url;
    },
    updateInvoiceItemsCount(state, invoiceitems_count) {
        state.invoiceitems_count = invoiceitems_count;
    },
    updateInvoiceItemsFilters(state, invoiceitems_filters) {
        state.invoiceitems_filters = invoiceitems_filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.invoiceitems_filters, fieldsToSave);
        }
    },
    updateInvoiceItemsLoading(state, invoiceitems_loading) {
        state.invoiceitems_loading = invoiceitems_loading;
    },
    updateInvoiceItemsLoadingErrors(state, invoiceitems_loading_errors) {
        state.invoiceitems_loading_errors = invoiceitems_loading_errors;
        state.invoiceitems_loading = false;
    },


    updateInvoiceItemsStats(state, invoiceitems_stats) {
        state.invoiceitems_stats = invoiceitems_stats;
        state.invoiceitems_stats_loaded = true;
    },
    updateInvoiceItemsStatsLoading(state, loading) {
        state.invoiceitems_stats_loading = loading;
    },
    updateInvoiceItemsStatsLoadingErrors(state, errors) {
        state.invoiceitems_stats_loading_errors = errors;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateCancelTokenSourceStats(state, source) {
        state.cancelTokenSourceStats = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },

};

const actions = {

    async fetchInvoiceItems({ commit, dispatch, state }, params) {
        if (
            !resolvePermissionCheck(
                store.getters['session/current_user_permissions'],
                CUSTOMER_INVOICE_PERMISSIONS.view
            )
        ) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateInvoiceItemsLoading', true);
        commit('updateInvoiceItemsLoadingErrors', null);

        try {
            const response = await axios.get(state.invoiceitems_url, {
                params: state.invoiceitems_filters,
                cancelToken: source.token,
            });
            commit('updateInvoiceItemsCount', response.data.count);
            commit('updateInvoiceItems', response.data.results);
            dispatch('fetchInvoiceItemsStats');
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateInvoiceItemsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateInvoiceItemsLoading', false);
        }
    },


    async fetchInvoiceItemsStats({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("billing.view_allocation") == -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSourceStats) {
            state.cancelTokenSourceStats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceStats', source);

        commit('updateInvoiceItemsStatsLoading', true);
        commit('updateInvoiceItemsStatsLoadingErrors', null);

        try {
            const response = await axios.get('/api/invoiceitems/stats/', {
                params: state.invoiceitems_filters,
                cancelToken: source.token,
            });
            commit('updateInvoiceItemsStatsLoading', false);
            commit('updateInvoiceItemsStats', response.data);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateInvoiceItemsStatsLoadingErrors', error.details);
            commit('updateInvoiceItemsStatsLoading', false);
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        if (!params.url) {
            throw new Error('Missing required parameter: url');
        }
        commit('updateInvoiceItemsUrl', params.url);
        commit('updateInvoiceItems', []);
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateInvoiceItemsFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params?.filters || {})
        });
    },
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions,
});
